/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo, useState} from 'react';
import IconButton from '@material-ui/core/IconButton';
import {makeStyles} from '@material-ui/core/styles';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import {translate} from '@ion/core/src/locale/locale';
import {ChevronExpandedIcon} from '@ion/dls/src/icons/ChevronExpandedIcon';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';

export interface JobConflictDevicesGridProps {
  onClose: () => void;
  devices: {
    deviceIdentifier: string;
    updateTime: string;
    taskStatus: string;
    jobId: number;
  }[];
}

const ITEMS_PER_PAGE = 10;

const useStyles = makeStyles((theme) => ({
  root : {
    width           : '100%',
    borderCollapse  : 'collapse',
    backgroundColor : dlsColor('grey8'),
    margin          : '10px 0px',
    tableLayout     : 'auto'
  },
  container : {
    width : '100%'
  },
  tableRow : {
    '& td' : {
      padding : '8px 0px'
    }
  },
  arrow : {
    cursor    : 'pointer',
    width     : '70px',
    '&:hover' : {
      '& svg' : {
        '& path' : {
          fill : dlsColor('grey2')
        }
      }
    }
  },
  label : {
    ...dlsAlt('typography', 'cta2'),
    fontWeight   : 500,
    fontSize     : '10px',
    color        : dlsColor('grey2'),
    marginBottom : 4,
    textAlign    : 'left',
    padding      : '0px'
  },
  value : {
    fontFamily : 'Roboto',
    fontSize   : '12px',
    lineHeight : '16px',
    color      : dlsColor('grey2')
  },
  statusValue : {
    fontFamily    : 'Roboto',
    fontSize      : '12px',
    lineHeight    : '16px',
    fontWeight    : 700,
    letterSpacing : '1px',
    color         : dlsColor('grey2')
  },
  tableContent : {
    width : '100%'
  },
  paginationContainer : {
    backgroundColor : dlsColor('white'),
    outline         : `1px solid ${dlsColor('grey5')}`,
    width           : '100%',
    display         : 'flex',
    justifyContent  : 'end',
    alignItems      : 'center',
    marginTop       : '10px'
  },
  paginationButton : {
    cursor        : 'pointer',
    fontFamily    : 'Roboto',
    fontSize      : '10px',
    fontWeight    : 400,
    letterSpacing : '1px',
    color         : dlsColor('grey2')
  }
}));

/**
 * JobConflictDevicesGrid component displays the nested devices table for the an individual job conflict.
 * 
 * ```typescript
 * import JobConflictDevicesGrid from './JobConflictDevicesGrid';
 * 
 * const devices = [{
 *   deviceIdentifier: '1234567890',
 *   updateTime: '2024-01-01T00:00:00Z',
 *   taskStatus: 'In Progress',
 * }];
 * 
 * <JobConflictDevicesGrid devices={devices} onClose={handleCollapse} />
 * 
 * ```
 * 
 * @param {Object[]} devices - List of devices involved in the job conflict.
 * @param {Function} onClose - Function that handles closing the nested devices table.
 */
const JobConflictDevicesGrid = ({devices, onClose}: JobConflictDevicesGridProps) => {
  const classes                       = useStyles();
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages                    = Math.ceil(devices.length / ITEMS_PER_PAGE);
  const startItem                     = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem                       = Math.min(currentPage * ITEMS_PER_PAGE, devices.length);

  const currentDevices = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return devices.slice(startIndex, endIndex);
  }, [currentPage, devices]);

  const handlePreviousPage = useCallback(function() {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  const handleNextPage = useCallback(function() {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);

  return (
    <div className={classes.container}>
      <table className={classes.root}>
        <thead>
          <tr>
            <th className={classes.arrow} onClick={onClose}>
              <ChevronExpandedIcon />
            </th>
            <th className={classes.label}>{translate('jobcreate:conflict.table.columns.identifier')}</th>
            <th className={classes.label}>{translate('jobcreate:conflict.table.columns.lastUpdate')}</th>
            <th className={classes.label}>{translate('jobcreate:conflict.table.columns.progressStatus')}</th>
          </tr>
        </thead>
        <tbody className={classes.tableContent}>
          {currentDevices.map((device) => (
            <tr key={`${device.jobId}-${device.deviceIdentifier}`} className={classes.tableRow}>
              <td />
              <td className={classes.value}>{device?.deviceIdentifier}</td>
              <td className={classes.value}>{device?.updateTime}</td>
              <td className={classes.statusValue}>{device?.taskStatus}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <ConditionalRender condition={devices?.length > ITEMS_PER_PAGE}>
        <div className={classes.paginationContainer}>
          <span className={classes.value}>
            {translate('jobcreate:conflict.table.pagination.rowsPerPage', {from: startItem, to: endItem, count: devices.length})}
          </span>
          <div>
            <IconButton onClick={handlePreviousPage} disabled={currentPage === 1}>
              <ChevronLeft />
            </IconButton>
            <span className={classes.value}>
              {currentPage} / {totalPages}
            </span>
            <IconButton onClick={handleNextPage} disabled={currentPage === totalPages}>
              <ChevronRight />
            </IconButton>
          </div>
        </div>
      </ConditionalRender>
    </div>
  );
};

export default JobConflictDevicesGrid;
